import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, TrendingUp, TrendingDown, Lock } from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";
import { asSentence, isWithheld } from "../utils/aiFleetPortfolio";

/**
 * The AI module's own posture score.
 *
 * Deliberately not a GRC surface: AI findings already flow into the risk register with financial
 * exposure, so scoring them again through the control pipeline would have counted the same
 * weakness twice in the number executives read. This scores the estate in place.
 *
 * The unassessed state is the one that matters. An organization with no AI is not at 0% — it has
 * nothing to grade, and showing a zero would read as a failing posture for an estate that does not
 * exist.
 */
const FAMILY_ORDER = [
  "AI Governance",
  "AI Asset Security",
  "Agentic AI Control",
  "AI Attack Defense",
];

function toneFor(score) {
  if (score === null || score === undefined) return "text-content-secondary";
  if (score >= 0.8) return "text-green-600";
  if (score >= 0.5) return "text-amber-600";
  return "text-red-600";
}

function pct(score) {
  return score === null || score === undefined ? "—" : `${Math.round(score * 100)}%`;
}

export default function AiPosturePanel({ refreshKey }) {
  const apiCall = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await apiCall("GET", "/ai-assets/posture");
        if (!cancelled) { setData(result); setError(null); }
      } catch (e) {
        // Rendering nothing on failure made a broken endpoint indistinguishable from a panel that
        // had decided it had nothing to say — which cost a round of "where is it?".
        if (!cancelled) { setData(null); setError(e?.message || "could not be loaded"); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (loading) return null;

  // A refusal for private-deal reasons is an answer with a reason, not a broken endpoint.
  if (isWithheld(data)) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-start gap-2 text-sm text-content-secondary">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-content-primary">AI posture withheld</div>
            <p>{asSentence(data.reason)}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-content-secondary">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-content-primary">AI posture unavailable</span>
          <span className="text-xs">{error ?? "no response"}</span>
        </div>
      </Card>
    );
  }

  if (!data.assessed) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-content-secondary">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-content-primary">AI posture not assessed</span>
          <span className="text-xs">{data.notAssessedReason}</span>
        </div>
      </Card>
    );
  }

  // First and last points of the retained trend — enough to say which way it is going without
  // implying more precision than a daily snapshot supports.
  const trend = data.trend ?? [];
  const earliest = trend.find((t) => t.overallScore !== null);
  const delta = earliest && data.overallScore !== null
    ? data.overallScore - earliest.overallScore
    : null;

  return (
    <Card className="p-4 mb-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-content-secondary" />
          <span className="text-sm font-semibold text-content-primary">AI posture</span>
          <span className="text-[11px] text-content-secondary">
            {data.assetCount} asset{data.assetCount === 1 ? "" : "s"} assessed
          </span>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-semibold ${toneFor(data.overallScore)}`}>
            {pct(data.overallScore)}
          </div>
          {delta !== null && Math.abs(delta) >= 0.01 && (
            <div className={`text-[11px] flex items-center gap-1 justify-end ${
              delta > 0 ? "text-green-600" : "text-red-600"
            }`}>
              {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {delta > 0 ? "+" : ""}{Math.round(delta * 100)} pts since {earliest.date}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-3">
        {FAMILY_ORDER.map((familyName) => {
          const family = (data.families ?? []).find((f) => f.family === familyName);
          if (!family) return null;
          const checks = (data.checks ?? []).filter((c) => c.family === familyName);
          return (
            <div key={familyName} className="py-1.5 border-b border-border last:border-0">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-content-secondary uppercase">
                  {familyName}
                </span>
                <span className={`text-sm font-medium ${toneFor(family.score)}`}>
                  {pct(family.score)}
                </span>
              </div>
              {checks.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-3 mt-1">
                  <div className="min-w-0">
                    <div className="text-sm text-content-primary">{c.title}</div>
                    <div className="text-xs text-content-secondary break-words">{c.evidence}</div>
                  </div>
                  <span className={`text-xs shrink-0 ${toneFor(c.score)}`}>{pct(c.score)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-content-secondary mt-3">
        Scored from this organization's own AI estate. Checks that do not apply — no agents, no
        telemetry-backed assets — are excluded rather than counted as failures.
      </p>
    </Card>
  );
}
